import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Card,
  Table,
  Tag,
  Modal,
  Form,
  DatePicker,
  Input,
  Select,
  Button,
  Drawer,
  Space,
} from "antd";
import { CloseOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import { getCustomerTransactions, getBranches } from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import {
  TransactionType,
  transactionTypes,
  UserType,
} from "../../../configs/constants";
import moment from "moment";
import { useTranslation } from "react-i18next";
import TransactionSlip from "../../../components/TransactionSlip/TransactionSlip";
import { handleApiError } from "../../../utils/errorHandler";
import ReactToPrint from "react-to-print";
import { useParams } from "react-router-dom";

const CustomerTransactions = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [transactionsTableData, setTransactionsTableData] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const [transaction, setTransaction] = useState({});
  const [searchParams, setSearchParams] = useState([]);
  const [slipModalVisible, setSlipModalVisible] = useState(false);
  const [cart, setCart] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [searchFormRef] = Form.useForm();
  const transactionSlipRef = useRef();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  let { customerId } = useParams();

  useEffect(() => {
    setIsDataLoading(true);
    getCustomerTransactions(customerId, {
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTransactionsTableData(response.data.data.transactions);
        setPaginationData({
          current: response.data.data.pagination.current_page,
          pageSize: response.data.data.pagination.per_page,
          total: response.data.data.pagination.total,
        });
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, customerId, perPageSize, reloadTableData, t]);

  useEffect(() => {
    let total = 0;

    let subtotal = 0;
    let productPrice = 0;
    cart.forEach((product) => {
      transaction.discount
        ? (productPrice =
            product.price -
            product.price * (transaction.discount.discount_percentage / 100))
        : (productPrice = product.price);

      subtotal = productPrice * product.quantity;
      total = total + subtotal;
    });
  }, [cart, transaction.discount]);

  useEffect(() => {
    if (transaction && transaction.items && transaction.items.length > 0) {
      setCart(JSON.parse(JSON.stringify(transaction.items)));
    }
  }, [transaction]);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  const showSlipModal = (transaction) => {
    setTransaction(transaction);
    setSlipModalVisible(true);
  };

  const hideSlipModal = () => {
    setSlipModalVisible(false);
    setTransaction({});
  };

  const getTransactionsType = (type) => {
    if (type === TransactionType.Cash) {
      return <Tag color="green">{t("cash")}</Tag>;
    } else if (type === TransactionType.Credit) {
      return <Tag color="blue">{t("credit_card")}</Tag>;
    } else if (type === TransactionType.Multipayment) {
      return <Tag color="gold">{t("multi_payment")}</Tag>;
    }
  };

  // firstPartCol Columns
  const firstPartCol = [
    {
      title: t("transaction_id"),
      dataIndex: "uid",
      render: (uid) => {
        return <p>{uid}</p>;
      },
    },
    {
      title: t("amount"),
      render: (record) => {
        return (
          <>
            {record.is_multipayment ? (
              record.multipayments.map((item, i) => {
                return (
                  <p key={i}>
                    {item.transaction_type}:{" "}
                    <CurrencyFormatter value={item.amount} />
                  </p>
                );
              })
            ) : (
              <p>
                <CurrencyFormatter value={record.amount_charged} />
              </p>
            )}
          </>
        );
      },
    },
    {
      title: t("taxes_on_sale"),
      dataIndex: "tax",
      render: (tax) => {
        return (
          <p>
            <CurrencyFormatter value={tax} />
          </p>
        );
      },
    },
  ];

  // Branch Columns
  const branchCol = [
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return <p>{branch}</p>;
      },
    },
  ];
  // secondPartCol Columns
  const secondPartCol = [
    {
      title: t("type"),
      dataIndex: "type",
      render: (type) => {
        return <p>{getTransactionsType(type)}</p>;
      },
    },
    {
      title: t("status"),
      render: (transaction) => {
        return (
          <p>
            {transaction.reference_transaction ? (
              <>
                <Tag color="error">{t("status_refunded")}</Tag>
                <br />
                {t("reference_no")}:{" "}
                <strong>{transaction.reference_transaction.uid}</strong>
              </>
            ) : (
              <Tag color="success">{t("status_paid")}</Tag>
            )}
          </p>
        );
      },
    },
    {
      title: `${t("created_at")} / ${t("created_by")}`,
      render: (record) => {
        return (
          <>
            <p>{moment(record.created_at).format("MMM. D, YYYY hh:mm a")}</p>
            {record.user && <Tag color="cyan">{record.user}</Tag>}
          </>
        );
      },
    },
    {
      title: t("action"),
      width: 85,
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              style={{ padding: 2 }}
              onClick={() => showSlipModal(record)}
            >
              <EyeOutlined title={t("view_details")} />
            </Button>
          </Space>
        );
      },
    },
  ];

  const handleSearch = (values) => {
    setDrawerVisible(false);
    let search_params = false;
    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });
      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }
    if (values.transaction_id) {
      search_params = {
        transaction_id: values.transaction_id,
        ...search_params,
      };
    }
    if (values.type) {
      search_params = {
        type: values.type,
        ...search_params,
      };
    }
    if (values.is_refunded === 0 || values.is_refunded) {
      search_params = {
        is_refunded: values.is_refunded,
        ...search_params,
      };
    }
    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
        ...search_params,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };

  const handleReset = () => {
    setDrawerVisible(false);
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = <></>;

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  return (
    <>
      <Card
        style={{
          direction: appConfig.direction
            ? appConfig.direction === "ltr"
              ? "ltr"
              : "rtl"
            : "ltr",
        }}
      >
        {searchForm}
        <Button
          type="primary"
          onClick={() => {
            setDrawerVisible(true);
          }}
        >
          <FilterOutlined />
        </Button>
      </Card>
      <Card>
        {Object.keys(searchParams).length !== 0 && (
          <div style={{ marginBottom: "15px" }}>
            {/* Transaction ID Tag */}
            {searchParams.transaction_id && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.transaction_id;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["transaction_id"]);
                }}
              >
                {t("transaction_id")}: {searchParams.transaction_id}
              </Tag>
            )}

            {/* Date Range Tag */}
            {searchParams.search_start_date && searchParams.search_end_date && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.search_start_date;
                  delete searchParams.search_end_date;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["dates"]);
                }}
              >
                {t("date_range")}:{" "}
                {moment(searchParams.search_start_date).format("MMM. D, YYYY")}{" "}
                - {moment(searchParams.search_end_date).format("MMM. D, YYYY")}
              </Tag>
            )}

            {/* Branch Filter Tag */}
            {searchParams.filter_by_branch && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.filter_by_branch;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["filter_by_branch"]);
                }}
              >
                {t("branch")}: {searchParams.filter_by_branch}
              </Tag>
            )}

            {/* Transaction Type Tag */}
            {searchParams.type && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.type;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["type"]);
                }}
              >
                {t("type")}:{" "}
                {searchParams.type === TransactionType.Cash
                  ? t("cash")
                  : searchParams.type === TransactionType.Credit
                    ? t("credit_card")
                    : searchParams.type === TransactionType.Multipayment
                      ? t("multi_payment")
                      : ""}
              </Tag>
            )}

            {/* Refund Status Tag */}
            {(searchParams.is_refunded === 0 ||
              searchParams.is_refunded === 1) && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.is_refunded;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["is_refunded"]);
                }}
              >
                {t("is_refunded")}:{" "}
                {searchParams.is_refunded ? t("yes") : t("no")}
              </Tag>
            )}
          </div>
        )}
        <Table
          columns={
            authUser.type === UserType.BusinessOwner
              ? [...firstPartCol, ...branchCol, ...secondPartCol]
              : [...firstPartCol, ...secondPartCol]
          }
          dataSource={transactionsTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
      <Modal
        open={slipModalVisible}
        title={t("transaction_id") + " # " + transaction.uid}
        onCancel={hideSlipModal}
        footer={[
          <ReactToPrint
            trigger={() => (
              <Button key="print" type="primary">
                {t("print")}
              </Button>
            )}
            content={() => transactionSlipRef.current}
            documentTitle={transaction.uid}
            key="1"
          />,
          <Button type="default" key="back" onClick={hideSlipModal}>
            {t("back")}
          </Button>,
        ]}
      >
        <TransactionSlip
          ref={transactionSlipRef}
          transaction={transaction}
          shop={{
            name: authUser.business_name,
            vat: authUser.vat,
            logo: authUser.logo,
            is_vat_exempt: authUser.is_vat_exempt,
          }}
        />
      </Modal>
      {/* Filter Drawer */}
      <Drawer
        title={t("filters")}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        onClose={() => {
          setDrawerVisible(false);
        }}
        open={drawerVisible}
        closable={false}
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setDrawerVisible(false);
            }}
          />
        }
      >
        <Form
          name="search-transaction-form"
          layout="vertical"
          onFinish={handleSearch}
          form={searchFormRef}
        >
          <Form.Item name="transaction_id" label={t("transaction_id")}>
            <Input allowClear />
          </Form.Item>
          <Form.Item name="dates" label={t("date_range")}>
            <RangePicker
              allowClear
              format="DD/MM/YYYY"
              className="app-w-full"
            />
          </Form.Item>
          <Form.Item name="filter_by_branch" label={t("branch")}>
            <Select
              allowClear
              options={branchesData.map((branch) => {
                return {
                  label: branch.name,
                  value: branch.name,
                };
              })}
            ></Select>
          </Form.Item>
          <Form.Item name="type" label={t("type")}>
            <Select
              allowClear
              options={transactionTypes.map((type) => {
                return {
                  label: t(type.translationKey),
                  value: type.id,
                };
              })}
            ></Select>
          </Form.Item>

          <Form.Item name="is_refunded" label={t("is_refunded")}>
            <Select
              allowClear
              options={[
                {
                  label: t("yes"),
                  value: 1,
                },
                {
                  label: t("no"),
                  value: 0,
                },
              ]}
            ></Select>
          </Form.Item>

          <Form.Item>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <Input
                type="submit"
                value={t("search")}
                className="app-search-submit"
                style={{ width: "100%" }}
              />
              <Input
                type="button"
                value={t("reset")}
                onClick={handleReset}
                style={{ width: "100%" }}
              />
            </div>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default CustomerTransactions;
